import { useEffect, useMemo, useRef, useState } from 'react'
import { useFrame } from '@react-three/fiber'
import * as THREE from 'three'
import { fighters } from './content'
import { impactBiologyFor } from './generatedImpactBiology'
import { useFightStore, type ImpactCue } from './store'

type BioImpact={id:number;cue:ImpactCue;targetId:string;createdAt:number}
let sharedImpactTexture:THREE.CanvasTexture|null=null
function impactTexture(){
 if(sharedImpactTexture)return sharedImpactTexture
 const canvas=document.createElement('canvas');canvas.width=64;canvas.height=64
 const ctx=canvas.getContext('2d')!,gradient=ctx.createRadialGradient(32,32,2,32,32,31)
 gradient.addColorStop(0,'rgba(255,255,255,1)');gradient.addColorStop(.32,'rgba(255,255,255,.82)');gradient.addColorStop(1,'rgba(255,255,255,0)')
 ctx.fillStyle=gradient;ctx.fillRect(0,0,64,64)
 sharedImpactTexture=new THREE.CanvasTexture(canvas);sharedImpactTexture.colorSpace=THREE.SRGBColorSpace
 return sharedImpactTexture
}
function rng(seed:number){let s=seed>>>0;return()=>{s=(s*1664525+1013904223)>>>0;return s/4294967296}}
function Burst({impact,onDone}:{impact:BioImpact;onDone:(id:number)=>void}){
 const profile=impactBiologyFor(impact.targetId)
 const texture=useMemo(()=>impactTexture(),[])
 const root=useRef<THREE.Group>(null)
 const mist=useRef<THREE.Sprite>(null)
 const decal=useRef<THREE.Mesh>(null)
 const finished=useRef(false)
 const droplets=useMemo(()=>{const r=rng(impact.cue.serial*2654435761);return Array.from({length:profile.droplets},(_,i)=>({x:(r()-.5)*1.25,y:.45+r()*1.15,z:(r()-.5)*.72,s:.025+r()*.055,spin:(r()-.5)*2.4,delay:r()*.16,index:i}))},[impact.cue.serial,profile.droplets])
 const life=Math.max(1.2,Math.min(4.5,profile.dissipation*.17))
 useFrame((_,dt)=>{
  if(!root.current)return
  const age=(performance.now()-impact.createdAt)/1000
  const p=THREE.MathUtils.clamp(age/life,0,1)
  root.current.rotation.y+=dt*.18
  if(mist.current){const m=mist.current.material as THREE.SpriteMaterial;m.opacity=(1-p)*(.34+profile.mist*.38);mist.current.scale.setScalar(.82+p*1.45)}
  if(decal.current){const m=decal.current.material as THREE.MeshBasicMaterial;m.opacity=(1-p)*.78}
  for(const child of root.current.children){if(!child.userData.bioDrop)continue;const d=child.userData.bioDrop as {vx:number;vy:number;vz:number;age:number};d.age+=dt;child.position.x+=d.vx*dt;child.position.z+=d.vz*dt;child.position.y=Math.max(.035,child.position.y+d.vy*dt);d.vy-=3.7*dt;child.rotation.x+=dt*5;child.rotation.z+=dt*4}
  if(age>=life&&!finished.current){finished.current=true;onDone(impact.id)}
 })
 const additive=profile.emissive>.55
 const direction=impact.cue.source==='player'?1:-1
 return <group ref={root} position={[impact.cue.x,0,impact.cue.z]}>
  <sprite ref={mist} position={[direction*.15,1.15,0]} scale={[.85,.85,.85]}>
   <spriteMaterial map={texture} color={profile.highlightColor} transparent opacity={.62} depthWrite={false} blending={additive?THREE.AdditiveBlending:THREE.NormalBlending}/>
  </sprite>
  <mesh ref={decal} rotation={[-Math.PI/2,0,(impact.cue.serial%11)*.31]} position={[direction*.12,.018,0]} scale={[1.1+profile.decalRadius*1.9,1.1+profile.decalRadius*1.9,1]}>
   <planeGeometry args={[1,1]}/>
   <meshBasicMaterial map={texture} color={profile.baseColor} transparent opacity={.78} depthWrite={false} polygonOffset polygonOffsetFactor={-2}/>
  </mesh>
  {droplets.map(d=><mesh key={d.index} position={[direction*(.08+d.x),d.y,d.z]} rotation={[d.spin,d.spin*.7,d.spin*.4]}
   userData={{bioDrop:{vx:direction*(.42+Math.abs(d.x)*.5),vy:.8+d.y*.45,vz:d.z*.55,age:-d.delay}}}>
   <sphereGeometry args={[d.s,6,5]}/>
   <meshBasicMaterial color={d.index%3===0?profile.highlightColor:profile.baseColor} transparent opacity={.9} blending={additive?THREE.AdditiveBlending:THREE.NormalBlending}/>
  </mesh>)}
  {profile.emissive>0&&<pointLight position={[0,1,0]} color={profile.highlightColor} intensity={Math.min(8,profile.emissive*2.4)} distance={2.8+profile.emissive} decay={2}/>}
 </group>
}

export function BiologicalImpactSystem(){
 const cue=useFightStore(s=>s.impactCue)
 const playerIndex=useFightStore(s=>s.playerIndex)
 const enemyIndex=useFightStore(s=>s.enemyIndex)
 const [impacts,setImpacts]=useState<BioImpact[]>([])
 const lastSerial=useRef(-1)
 useEffect(()=>{
  if(!cue||lastSerial.current===cue.serial||!['hit','throw'].includes(cue.kind))return
  lastSerial.current=cue.serial
  const targetId=cue.source==='player'?fighters[enemyIndex]?.id:fighters[playerIndex]?.id
  if(!targetId)return
  setImpacts(current=>[...current.slice(-9),{id:cue.serial,cue,targetId,createdAt:performance.now()}])
 },[cue,playerIndex,enemyIndex])
 const remove=(id:number)=>setImpacts(current=>current.filter(item=>item.id!==id))
 return <group name="impact-biology-runtime">{impacts.map(impact=><Burst key={impact.id} impact={impact} onDone={remove}/>)}</group>
}
